import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Button,
  Grid,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Chip,
  CircularProgress,
  Alert,
  Paper,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  MenuItem,
  Menu,
  IconButton,
  Divider,
  ListItemIcon,
  Tooltip,
  Stack
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Event as EventIcon,
  LocationOn as LocationIcon,
  People as PeopleIcon,
  MoreVert as MoreIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Share as ShareIcon,
  AttachMoney as MoneyIcon,
  CreditCard as PaymentIcon
} from '@mui/icons-material';
import { useOrganization } from '../../../contexts/OrganizationContext';
import { collection, query, where, orderBy, getDocs, Timestamp, onSnapshot } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { format } from 'date-fns';
import { getDefaultBackgroundStyle } from '../../../utils/defaultImages';

// Event location type
interface LocationType {
  name?: string;
  address?: string;
  coordinates?: [number, number];
}

// Event interface to ensure consistent typing
interface EventItem {
  id: string;
  title: string;
  description?: string;
  startDate: Date;
  endDate?: Date | null;
  location?: string | LocationType;
  coverPhotoUrl?: string;
  isPublic: boolean;
  eventType?: string;
  participants?: any[];
  createdBy?: string;
  organizationId: string;
  isPaid?: boolean;
  ticketTypes?: any[];
  ticketsSold?: number;
  maxCapacity?: number;
}

const OrganizationEvents: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { activeOrganization, isPersonalAccount, switchToOrganization } = useOrganization();
  
  const [events, setEvents] = useState<EventItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  
  // Function to fetch events with real-time updates
  const fetchEvents = useCallback(async () => {
    if (!id) return;
    
    try {
       setLoading(true);

       // Switch to organization context if needed
       if (isPersonalAccount) {
          await switchToOrganization(id);
       }

       // Create the base query for events with this organization ID
       const eventsRef = collection(db, 'events');
       let eventsQuery;

       const now = Timestamp.fromDate(new Date());

       // Different queries based on tab selection
       if (tabValue === 0) {
          // Upcoming events
          eventsQuery = query(
             eventsRef,
             where('organizationId', '==', id),
             // Don't filter by date for now to see all events
             orderBy('startDate', 'asc')
          );
       } else if (tabValue === 1) {
          // Past events
          eventsQuery = query(
             eventsRef,
             where('organizationId', '==', id),
             where('startDate', '<', now),
             orderBy('startDate', 'desc')
          );
       } else {
          // All events
          eventsQuery = query(
             eventsRef,
             where('organizationId', '==', id),
             orderBy('startDate', 'desc')
          );
       }

       // Set up real-time listener for events
       const unsubscribe = onSnapshot(
          eventsQuery,
          (snapshot) => {
             const eventsData: EventItem[] = [];

             snapshot.forEach((doc) => {
                const data = doc.data();

                // Convert Firestore timestamps to JavaScript dates
                const startDate =
                   data.startDate instanceof Timestamp
                      ? data.startDate.toDate()
                      : new Date(data.startDate);

                const endDate = data.endDate
                   ? data.endDate instanceof Timestamp
                      ? data.endDate.toDate()
                      : new Date(data.endDate)
                   : null;

                eventsData.push({
                   id: doc.id,
                   title: data.title,
                   description: data.description,
                   startDate,
                   endDate,
                   location: data.location,
                   coverPhotoUrl: data.coverPhotoUrl,
                   isPublic: data.isPublic,
                   eventType: data.eventType,
                   participants: data.participants || [],
                   createdBy: data.createdBy,
                   organizationId: data.organizationId,
                   isPaid: data.isPaid,
                   ticketTypes: data.ticketTypes,
                   ticketsSold: data.ticketsSold || 0,
                   maxCapacity: data.maxCapacity,
                });
             });

             // Apply search filter if there is a query
             const filteredEvents = searchQuery
                ? eventsData.filter(
                     (event) =>
                        event.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                        (event.description &&
                           event.description.toLowerCase().includes(searchQuery.toLowerCase())) ||
                        (typeof event.location === 'string' &&
                           event.location.toLowerCase().includes(searchQuery.toLowerCase())) ||
                        (typeof event.location === 'object' &&
                           event.location?.name?.toLowerCase().includes(searchQuery.toLowerCase()))
                  )
                : eventsData;

             setEvents(filteredEvents);
             setLoading(false);
          },
          (err) => {
             console.error('Error fetching organization events:', err);
             setError('Failed to load events. Please try again.');
             setLoading(false);
          }
       );

       // Clean up listener on unmount or when dependencies change
       return () => unsubscribe();
    } catch (err) {
      console.error('Error setting up events listener:', err);
      setError('Failed to load events');
      setLoading(false);
    }
  }, [id, isPersonalAccount, switchToOrganization, tabValue, searchQuery]);
  
  useEffect(() => {
    let unsubscribeFunction: (() => void) | undefined;
    
    fetchEvents().then(cleanupFn => {
      unsubscribeFunction = cleanupFn;
    });
    
    return () => {
      if (unsubscribeFunction) unsubscribeFunction();
    };
  }, [fetchEvents]);
  
  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };
  
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };
  
  const handleCreateEvent = () => {
    navigate(`/organizations/${id}/events/create`);
  };
  
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, eventId: string) => {
    setMenuAnchorEl(event.currentTarget);
    setSelectedEventId(eventId);
  };
  
  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };
  
  const handleEditEvent = () => {
    if (selectedEventId) {
      navigate(`/organizations/${id}/events/edit/${selectedEventId}`);
    }
    handleMenuClose();
  };
  
  const handleViewEvent = () => {
    if (selectedEventId) {
      navigate(`/organizations/${id}/events/${selectedEventId}`);
    }
    handleMenuClose();
  };
  
  const formatEventDate = (startDate: Date, endDate: Date | null | undefined) => {
    if (!startDate) return 'Date not set';
    
    const startStr = format(startDate, 'MM/dd/yyyy');
    const timeStr = format(startDate, 'h:mm a');
    
    if (!endDate) return `${startStr} at ${timeStr}`;
    
    if (format(startDate, 'MM/dd/yyyy') === format(endDate, 'MM/dd/yyyy')) {
      return `${startStr} ${format(startDate, 'h:mm a')} - ${format(endDate, 'h:mm a')}`;
    }
    
    return `${startStr} - ${format(endDate, 'MM/dd/yyyy')}`;
  };
  
  const formatLocation = (location: string | LocationType | undefined) => {
    if (!location) return 'Location not set';
    if (typeof location === 'string') return location;
    return location.name || location.address || 'Location details not provided';
  };
  
  return (
     <Container maxWidth='lg' sx={{ py: 4 }}>
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
           <Typography variant='h4' component='h1'>
              {activeOrganization?.name || 'Organization'} Events
           </Typography>

           <Button
              variant='contained'
              color='primary'
              startIcon={<AddIcon />}
              onClick={handleCreateEvent}
           >
              Create Event
           </Button>
        </Box>

        <Typography variant='body1' color='text.secondary' sx={{ mb: 4 }}>
           Manage your organization's events, including paid ticket events
        </Typography>

        {error && (
           <Alert severity='error' sx={{ mb: 3 }}>
              {error}
           </Alert>
        )}

        <Paper sx={{ mb: 4, borderRadius: 2 }}>
           <Box sx={{ px: 3, pt: 3, pb: 1 }}>
              <Grid container spacing={2} alignItems='center'>
                 <Grid item xs={12} sm={6} md={8}>
                    <TextField
                       fullWidth
                       placeholder='Search events...'
                       value={searchQuery}
                       onChange={handleSearchChange}
                       InputProps={{
                          startAdornment: (
                             <InputAdornment position='start'>
                                <SearchIcon />
                             </InputAdornment>
                          ),
                       }}
                       variant='outlined'
                       size='small'
                    />
                 </Grid>
                 <Grid item xs={12} sm={6} md={4}>
                    <Tabs
                       value={tabValue}
                       onChange={handleTabChange}
                       variant='fullWidth'
                       textColor='primary'
                       indicatorColor='primary'
                    >
                       <Tab label='Upcoming' />
                       <Tab label='Past' />
                       <Tab label='All' />
                    </Tabs>
                 </Grid>
              </Grid>
           </Box>

           <Divider />

           {loading ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                 <CircularProgress />
              </Box>
           ) : events.length === 0 ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                 <Typography variant='body1' color='text.secondary' sx={{ mb: 2 }}>
                    {searchQuery
                       ? 'No events found matching your search'
                       : tabValue === 0
                         ? 'No upcoming events'
                         : tabValue === 1
                           ? 'No past events'
                           : 'No events found'}
                 </Typography>
                 <Button variant='outlined' startIcon={<AddIcon />} onClick={handleCreateEvent}>
                    Create Your First Event
                 </Button>
              </Box>
           ) : (
              <Box sx={{ p: 3 }}>
                 <Grid container spacing={3}>
                    {events.map((event) => (
                       <Grid item xs={12} sm={6} md={4} key={event.id}>
                          <Card
                             sx={{
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                borderRadius: 2,
                                transition: 'transform 0.2s, box-shadow 0.2s',
                                cursor: 'pointer',
                                '&:hover': {
                                   transform: 'translateY(-4px)',
                                   boxShadow: 3,
                                },
                             }}
                             onClick={() => {
                                navigate(`/organizations/${id}/events/${event.id}`);
                             }}
                          >
                             <Box
                                sx={{
                                   height: 160,
                                   position: 'relative',
                                   ...getDefaultBackgroundStyle(
                                      'event',
                                      event.id,
                                      event.coverPhotoUrl
                                   ),
                                   display: 'flex',
                                   alignItems: 'center',
                                   justifyContent: 'center',
                                }}
                             >
                                {!event.coverPhotoUrl && (
                                   <>
                                      <Box
                                         sx={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            right: 0,
                                            bottom: 0,
                                            background: 'rgba(0, 0, 0, 0.3)',
                                            zIndex: 1,
                                         }}
                                      />
                                      <Box
                                         sx={{
                                            textAlign: 'center',
                                            position: 'relative',
                                            zIndex: 2,
                                         }}
                                      >
                                         <EventIcon
                                            sx={{
                                               fontSize: 40,
                                               color: 'white',
                                               mb: 1,
                                               opacity: 0.9,
                                            }}
                                         />
                                         <Typography
                                            variant='caption'
                                            sx={{ color: 'white', opacity: 0.9, display: 'block' }}
                                         >
                                            {event.title}
                                         </Typography>
                                      </Box>
                                   </>
                                )}
                             </Box>

                             <CardContent sx={{ flexGrow: 1 }}>
                                <Typography variant='h6' component='h2' gutterBottom noWrap>
                                   {event.title}
                                </Typography>

                                <Stack direction='row' spacing={1} sx={{ mb: 1 }}>
                                   <Chip
                                      size='small'
                                      label={event.eventType || 'Event'}
                                      color='primary'
                                      variant='outlined'
                                   />
                                   {event.isPaid && (
                                      <Chip
                                         size='small'
                                         label='Paid'
                                         color='secondary'
                                         icon={<MoneyIcon />}
                                         variant='outlined'
                                      />
                                   )}
                                </Stack>

                                <Typography variant='body2' color='text.secondary' gutterBottom>
                                   <EventIcon
                                      fontSize='small'
                                      sx={{ verticalAlign: 'middle', mr: 0.5, fontSize: '1rem' }}
                                   />
                                   {formatEventDate(event.startDate, event.endDate)}
                                </Typography>

                                <Typography
                                   variant='body2'
                                   color='text.secondary'
                                   gutterBottom
                                   noWrap
                                >
                                   <LocationIcon
                                      fontSize='small'
                                      sx={{ verticalAlign: 'middle', mr: 0.5, fontSize: '1rem' }}
                                   />
                                   {formatLocation(event.location)}
                                </Typography>

                                {event.isPaid && (
                                   <Typography variant='body2' color='text.secondary'>
                                      <PeopleIcon
                                         fontSize='small'
                                         sx={{ verticalAlign: 'middle', mr: 0.5, fontSize: '1rem' }}
                                      />
                                      {event.ticketsSold || 0} / {event.maxCapacity || '∞'} tickets
                                      sold
                                   </Typography>
                                )}
                             </CardContent>

                             <CardActions>
                                <IconButton
                                   size='small'
                                   onClick={(e) => handleMenuOpen(e, event.id)}
                                   sx={{ ml: 'auto' }}
                                >
                                   <MoreIcon />
                                </IconButton>
                             </CardActions>
                          </Card>
                       </Grid>
                    ))}
                 </Grid>
              </Box>
           )}
        </Paper>

        {/* Event Actions Menu */}
        <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={handleMenuClose}>
           <MenuItem onClick={handleViewEvent}>
              <ListItemIcon>
                 <EventIcon fontSize='small' />
              </ListItemIcon>
              View Details
           </MenuItem>
           <MenuItem onClick={handleEditEvent}>
              <ListItemIcon>
                 <EditIcon fontSize='small' />
              </ListItemIcon>
              Edit Event
           </MenuItem>
           <MenuItem onClick={handleMenuClose}>
              <ListItemIcon>
                 <ShareIcon fontSize='small' />
              </ListItemIcon>
              Share
           </MenuItem>
           <Divider />
           <MenuItem onClick={handleMenuClose} sx={{ color: 'error.main' }}>
              <ListItemIcon>
                 <DeleteIcon fontSize='small' color='error' />
              </ListItemIcon>
              Delete
           </MenuItem>
        </Menu>
     </Container>
  );
};

export default OrganizationEvents;